import { SearchIcon, XIcon } from "@heroicons/react/solid";
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react";

import { Select, SelectOption } from "@components/select/select";

import IntegrationCard, {
  ORGANIZATION_INTEGRATION_ADMIN_TOOLTIP,
} from "./integrations-card";
import {
  ALL_INTEGRATIONS_CATEGORY,
  getIntegrationCategoryLabel,
  normalizeIntegrationCategory,
  sortIntegrationCategories,
} from "./integrations-constants";

export type IntegrationCardData = {
  slug: string;
  name: string;
  logo: string;
  logoNode?: ReactNode;
  summary: string;
  categories: readonly string[];
};

const IntegrationsBrowser = ({
  enabled,
  available,
  onSelect,
  topSlot,
  availableStatusLabel = "Connect",
  availableStatusTone = "neutral",
}: {
  enabled: IntegrationCardData[];
  available: IntegrationCardData[];
  onSelect?: (slug: string) => void;
  topSlot?: ReactNode;
  availableStatusLabel?: string;
  availableStatusTone?: "enabled" | "neutral" | "warning";
}) => {
  const [activeCategory, setActiveCategory] = useState(
    ALL_INTEGRATIONS_CATEGORY,
  );
  const [searchQuery, setSearchQuery] = useState("");

  const categories = useMemo(
    () =>
      sortIntegrationCategories(
        available.flatMap((integration) =>
          integration.categories.map(normalizeIntegrationCategory),
        ),
      ),
    [available],
  );

  const categoryOptions: SelectOption<string>[] = useMemo(
    () => [
      { value: ALL_INTEGRATIONS_CATEGORY, label: "All" },
      ...categories.map((category) => ({
        value: category,
        label: getIntegrationCategoryLabel(category),
      })),
    ],
    [categories],
  );

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const visibleAvailable = useMemo(
    () =>
      available.filter((integration) => {
        const normalizedCategories = integration.categories.map(
          normalizeIntegrationCategory,
        );
        const matchesSearch =
          !normalizedSearchQuery ||
          [
            integration.name,
            integration.slug,
            integration.summary,
            ...normalizedCategories,
            ...normalizedCategories.map(getIntegrationCategoryLabel),
          ].some((value) =>
            value.toLowerCase().includes(normalizedSearchQuery),
          );
        const matchesCategory =
          normalizedSearchQuery ||
          activeCategory === ALL_INTEGRATIONS_CATEGORY ||
          normalizedCategories.includes(activeCategory);
        return matchesSearch && matchesCategory;
      }),
    [activeCategory, available, normalizedSearchQuery],
  );

  useEffect(() => {
    if (
      activeCategory !== ALL_INTEGRATIONS_CATEGORY &&
      !categories.includes(activeCategory)
    ) {
      setActiveCategory(ALL_INTEGRATIONS_CATEGORY);
    }
  }, [activeCategory, categories]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    if (event.target.value.trim()) {
      setActiveCategory(ALL_INTEGRATIONS_CATEGORY);
    }
  };

  return (
    <div className="space-y-8">
      {topSlot}

      {enabled.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {enabled.map((integration) => (
            <IntegrationCard
              key={integration.slug}
              name={integration.name}
              logo={integration.logo}
              logoNode={integration.logoNode}
              summary={integration.summary}
              statusLabel="Enabled"
              statusTone="enabled"
              isDisabled={!onSelect}
              disabledTooltip={
                !onSelect ? ORGANIZATION_INTEGRATION_ADMIN_TOOLTIP : undefined
              }
              onClick={() => onSelect?.(integration.slug)}
            />
          ))}
        </div>
      )}

      <div className="space-y-2">
        <div className="sticky top-0 z-10 bg-white pt-1 pb-2 flex flex-wrap items-center gap-3">
          <label className="relative block w-full sm:flex-1">
            <span className="sr-only">Search integrations</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={(event) => event.currentTarget.select()}
              placeholder="Search integrations..."
              className="h-8 w-full rounded-md border border-gray-200 bg-white pl-9 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <XIcon className="size-4" />
              </button>
            )}
          </label>
          <Select
            prefix="Category"
            options={categoryOptions}
            value={activeCategory}
            onChange={(option) => setActiveCategory(option.value)}
            showSelectedIcon={false}
            containerClassName="w-full sm:w-auto sm:min-w-[16rem]"
            aria-label="Filter by category"
          />
        </div>

        {visibleAvailable.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            No integrations match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleAvailable.map((integration) => (
              <IntegrationCard
                key={integration.slug}
                name={integration.name}
                logo={integration.logo}
                logoNode={integration.logoNode}
                summary={integration.summary}
                statusLabel={availableStatusLabel}
                statusTone={availableStatusTone}
                isDisabled={!onSelect}
                disabledTooltip={
                  !onSelect ? ORGANIZATION_INTEGRATION_ADMIN_TOOLTIP : undefined
                }
                onClick={() => onSelect?.(integration.slug)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationsBrowser;
